/**
 * De aanpasbare cellen van de materiaalstaat.
 *
 * Twee soorten, precies zoals gevraagd:
 *  - `Cel` — maatvoering, benaming, aantal en opmerkingen: klikken, typen, Enter. Bij
 *    een maat kan ook de overmaat voor karwei worden ingevuld; die maakt de maat
 *    blauw.
 *  - `Keuzecel` — materiaal en de afwerking per zijde: klikken opent een menu met alle
 *    mogelijkheden, met een zoekveld omdat de lijsten lang zijn.
 *
 * Staat het bouwelement op produceren, dan is de houtstaat vergrendeld: de cel is dan
 * niet aanpasbaar en legt uit waarom. De server weigert de wijziging ook.
 */
import { useEffect, useRef, useState } from 'react';

import { Dialoog } from './Dialoog';
import { IconZoek } from './Iconen';

interface CelProps {
  waarde: string | number | undefined;
  /** Getoonde tekst; standaard de waarde zelf. */
  weergave?: string;
  soort?: 'tekst' | 'getal';
  /** Blauw: er zit overmaat voor karwei in deze maat. */
  karwei?: boolean;
  /** Rood en golvend onderstreept: openstaande rode waarde op dit veld. */
  fout?: boolean;
  vergrendeld?: boolean;
  vergrendelUitleg?: string;
  magAanpassen: boolean;
  onOpslaan: (waarde: string) => void;
  rechts?: boolean;
  titel?: string;
}

export function Cel({
  waarde,
  weergave,
  soort = 'tekst',
  karwei,
  fout,
  vergrendeld,
  vergrendelUitleg,
  magAanpassen,
  onOpslaan,
  rechts,
  titel,
}: CelProps) {
  const [bewerken, setBewerken] = useState(false);
  const [concept, setConcept] = useState(String(waarde ?? ''));
  const veld = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (bewerken) veld.current?.select();
  }, [bewerken]);

  const tekst = weergave ?? (waarde == null || waarde === '' ? '—' : String(waarde));
  const aanpasbaar = magAanpassen && !vergrendeld;

  if (bewerken) {
    return (
      <input
        ref={veld}
        className="hs-celinvoer"
        type={soort === 'getal' ? 'number' : 'text'}
        step="any"
        value={concept}
        style={rechts ? { textAlign: 'right' } : undefined}
        onChange={(e) => setConcept(e.target.value)}
        onBlur={() => {
          setBewerken(false);
          if (concept !== String(waarde ?? '')) onOpslaan(concept);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          } else if (e.key === 'Escape') {
            setConcept(String(waarde ?? ''));
            setBewerken(false);
          }
        }}
      />
    );
  }

  return (
    <button
      type="button"
      className="hs-cel"
      disabled={!aanpasbaar}
      style={rechts ? { textAlign: 'right' } : undefined}
      title={vergrendeld ? vergrendelUitleg : (titel ?? (aanpasbaar ? 'Klik om aan te passen' : undefined))}
      onClick={() => {
        setConcept(String(waarde ?? ''));
        setBewerken(true);
      }}
    >
      {karwei ? (
        <span className="hs-karwei hs-mono">{tekst}</span>
      ) : fout ? (
        <span className="hs-fout-waarde hs-mono">{tekst}</span>
      ) : (
        tekst
      )}
    </button>
  );
}

interface KeuzecelProps {
  waarde: string;
  /** Alle mogelijkheden; de lijst is doorzoekbaar. */
  opties: string[];
  titel: string;
  fout?: boolean;
  vergrendeld?: boolean;
  vergrendelUitleg?: string;
  magAanpassen: boolean;
  onOpslaan: (waarde: string) => void;
  /** Leeg toestaan (geen afwerking, geen kantenband). */
  metLeeg?: boolean;
}

export function Keuzecel({
  waarde,
  opties,
  titel,
  fout,
  vergrendeld,
  vergrendelUitleg,
  magAanpassen,
  onOpslaan,
  metLeeg = true,
}: KeuzecelProps) {
  const [open, setOpen] = useState(false);
  const [zoek, setZoek] = useState('');
  const aanpasbaar = magAanpassen && !vergrendeld;

  const gevonden = opties.filter((o) => o.toLowerCase().includes(zoek.trim().toLowerCase()));

  return (
    <>
      <button
        type="button"
        className="hs-cel hs-mono"
        disabled={!aanpasbaar}
        title={vergrendeld ? vergrendelUitleg : aanpasbaar ? `${titel} kiezen` : undefined}
        onClick={() => {
          setZoek('');
          setOpen(true);
        }}
      >
        {fout ? (
          <span className="hs-fout-waarde">{waarde || '—'}</span>
        ) : (
          waarde || <span className="hs-muted-licht">—</span>
        )}
      </button>

      {open ? (
        <Dialoog titel={titel} onSluiten={() => setOpen(false)} breedte={420}>
          <label className="hs-veld">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <IconZoek maat={12} /> Zoeken in {opties.length} mogelijkheden
            </span>
            <input
              className="hs-invoer hs-invoer--mono"
              value={zoek}
              autoFocus
              onChange={(e) => setZoek(e.target.value)}
              placeholder="code of naam"
            />
          </label>

          <div style={{ maxHeight: 320, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {metLeeg ? (
              <button
                type="button"
                className={`hs-knop${waarde === '' ? ' hs-knop--primair' : ''}`}
                style={{ justifyContent: 'flex-start' }}
                onClick={() => {
                  onOpslaan('');
                  setOpen(false);
                }}
              >
                — geen —
              </button>
            ) : null}
            {gevonden.map((optie) => (
              <button
                key={optie}
                type="button"
                className={`hs-knop hs-mono${optie === waarde ? ' hs-knop--primair' : ''}`}
                style={{ justifyContent: 'flex-start' }}
                onClick={() => {
                  onOpslaan(optie);
                  setOpen(false);
                }}
              >
                {optie}
              </button>
            ))}
            {gevonden.length === 0 ? (
              <p className="hs-muted" style={{ padding: '8px 2px' }}>
                Niets gevonden. Ontbreekt de code echt, dan moet hij eerst in de coderingen worden
                toegevoegd.
              </p>
            ) : null}
          </div>
        </Dialoog>
      ) : null}
    </>
  );
}
